import React, { useState } from 'react';

import { Button, Flex, ScrollArea, Stack, TextInput } from '@mantine/core';
import { IconClipboardCheck, IconPlus, IconSearch } from '@tabler/icons-react';
import { NoDataFound } from 'components/NoDataFound';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { CreateEvaluationModal } from 'pages/ManageSmartAssistant/Sections/EvaluationsSection/EvaluationModal/CreateEvaluationModal';
import { ResetEvaluationModal } from 'pages/ManageSmartAssistant/Sections/EvaluationsSection/EvaluationModal/ResetEvaluationModal';
import { EvaluationTile } from 'pages/ManageSmartAssistant/Sections/EvaluationsSection/EvaluationTile';
import { useTranslation } from 'react-i18next';
import { Evaluation } from 'shared/interfaces/Evaluation';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface EvaluationsListViewProps {
  smartAssistant: SmartAssistant;
  evaluations: Evaluation[];
  onEvaluationClick: (evaluation: Evaluation) => void;
  handleRunAllTests: (evaluationId: string) => void;
  isGuest?: boolean;
}

export function EvaluationsListView({
  smartAssistant,
  evaluations,
  onEvaluationClick,
  handleRunAllTests,
  isGuest = false
}: EvaluationsListViewProps) {
  const { t } = useTranslation();

  const [searchFilter, setSearchFilter] = useState<string>('');
  const [showEvaluationModal, setShowEvaluationModal] = useState<boolean>(false);
  const [evaluationBeingEdited, setEvaluationBeingEdited] = useState<Evaluation | null>(null);

  const closeEvaluationModal = () => {
    setShowEvaluationModal(false);
    setEvaluationBeingEdited(null);
  };

  const openEvaluationModal = () => {
    setShowEvaluationModal(true);
    setEvaluationBeingEdited(null);
  };

  const openEditEvaluationModal = (evaluation: Evaluation) => {
    setEvaluationBeingEdited(evaluation);
    setShowEvaluationModal(true);
  };

  const filteredEvaluations = evaluations
    .filter((evaluation) => evaluation.name.toLowerCase().includes(searchFilter.toLowerCase()))
    // Reverse the evaluations to show the latest ones first
    .reverse();

  return (
    <ManageSmartAssistantSectionControlWrapper title={t('SMART_ASSISTANT.EVALUATIONS_SECTION.TITLE')}>
      <Stack spacing="md" w="100%" h="100%">
        <Flex justify="space-between" align="center" gap="md">
          <TextInput
            icon={<IconSearch size={16} />}
            placeholder={t('SMART_ASSISTANT.EVALUATIONS_SECTION.SEARCH_PLACEHOLDER')}
            style={{ flexGrow: 1 }}
            value={searchFilter}
            onChange={(event) => setSearchFilter(event.target.value)}
          />
          {!isGuest && (
            <Button variant="filled" leftIcon={<IconPlus />} onClick={openEvaluationModal}>
              {t('SMART_ASSISTANT.EVALUATIONS_SECTION.CREATE_EVALUATION')}
            </Button>
          )}
        </Flex>

        <ScrollArea scrollbarSize={0} style={{ flexGrow: 1 }}>
          <Stack spacing="md" h="100%">
            {evaluations.length === 0 && (
              <NoDataFound
                h="100%"
                size="xs"
                icon={<IconClipboardCheck />}
                title={t('SMART_ASSISTANT.EVALUATIONS_SECTION.NO_EVALUATIONS')}
                subTitle={t('SMART_ASSISTANT.EVALUATIONS_SECTION.NO_EVALUATIONS_SUBTITLE')}
              />
            )}

            {filteredEvaluations.length > 0 &&
              filteredEvaluations.map((evaluation, index) => (
                <EvaluationTile
                  key={index}
                  evaluation={evaluation}
                  smartAssistantId={smartAssistant._id}
                  onEdit={openEditEvaluationModal}
                  onClick={() => onEvaluationClick(evaluation)}
                  handleRunAllTests={handleRunAllTests}
                  isGuest={isGuest}
                />
              ))}
          </Stack>
        </ScrollArea>
      </Stack>

      {evaluationBeingEdited ? (
        <ResetEvaluationModal
          opened={showEvaluationModal}
          onClose={closeEvaluationModal}
          smartAssistant={smartAssistant}
          evaluation={evaluationBeingEdited}
        />
      ) : (
        <CreateEvaluationModal
          opened={showEvaluationModal}
          onClose={closeEvaluationModal}
          smartAssistant={smartAssistant}
        />
      )}
    </ManageSmartAssistantSectionControlWrapper>
  );
}
